import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import type { CallGroup, OriginateCallRequest } from "@/types";
import { callsApi } from "@/lib/api";

export interface CallHistoryFilters {
  direction?: string;
  date_from?: string;
  date_to?: string;
}

export function useActiveCalls(enabled = true) {
  return useQuery({
    queryKey: ["calls", "active"],
    queryFn: callsApi.active,
    refetchInterval: 5000,
    enabled,
  });
}

/**
 * A page of *calls*, not of channels: `GET /api/calls` groups the `call_log`
 * rows by `linked_id` and returns one `CallGroup` per call, with the per-leg
 * rows nested under `channels`. Pagination is over calls too, so `limit` is a
 * number of calls.
 *
 * The assertion is here because `callsApi` is still declared as returning the
 * per-channel `CallLog`; `CallGroup` extends it, so this only adds the two new
 * fields the endpoint now sends.
 */
export function useCallHistory(skip = 0, limit = 50, filters: CallHistoryFilters = {}) {
  return useQuery({
    queryKey: ["calls", "history", skip, limit, filters],
    queryFn: () =>
      callsApi.history({ skip, limit, ...filters }) as Promise<CallGroup[]>,
    // Changing a filter or a page is a new cache key, so without this the table
    // has no data for it and drops to the loading skeleton — the rows vanish on
    // every click and the request takes a second or two to fill them back in.
    // Keeping the previous page visible makes a filter change read as a change
    // rather than as "nothing showing up".
    placeholderData: keepPreviousData,
  });
}

/** `COUNT(DISTINCT linked_id)` — a number of calls, not of channels. */
export function useCallHistoryCount(filters: CallHistoryFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ["calls", "count", filters],
    queryFn: () => callsApi.historyCount(filters),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** One call by its id — the `linked_id` string returned by `useCallHistory`. */
export function useCall(id: string) {
  return useQuery({
    queryKey: ["calls", id],
    queryFn: () => callsApi.get(id) as Promise<CallGroup>,
    enabled: !!id,
  });
}

export function useHangupCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channel: string) => callsApi.hangup(channel),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls"] });
    },
  });
}

export function useTransferCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channel, destination }: { channel: string; destination: string }) =>
      callsApi.transfer(channel, destination),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls"] });
    },
  });
}

export function useOriginateCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: OriginateCallRequest) => callsApi.originate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls"] });
    },
  });
}

export function useExtensions() {
  return useQuery({
    queryKey: ["calls", "extensions"],
    queryFn: callsApi.extensions,
  });
}
